import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Heading, Icon } from '../../common';
import { NavTab, NavTabs, NavTabsContainer } from '../../common/NewTab';
import PageLoader2 from '../PageLoader2';
import VideoCall from './VideoCall';
import GuidedScreen from './GuidedScreen';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { REQUEST_CAPTURE_GUIDE } from '../../constants/field/GetCaptureGuideConstants';

const CustomButton = styled(Button)`
  color: var(--sapGreen);
  .material-icons {
    color: var(--sapGreen);
  }
  :hover {
    .material-icons {
      color: var(--white);
    }
  }
  :focus {
    background: transparent;
    color: var(--sapGreen);
    outline: none;
    box-shadow: none;
    :hover {
      background: var(--sapGreen);
      color: var(--white);
    }
  }
  :disabled,
  [disabled] {
    background: transparent;
    border-color: var(--middleGrey);
    color: var(--middleGrey);
    .material-icons {
      color: var(--middleGrey);
    }
    :hover {
      box-shadow: none;
      background: #c2c2c2;
      color: #ffffff;
      cursor: not-allowed;
      .material-icons {
        color: var(--white);
      }
    }
  }
`;

const Container = styled.div`
  padding: 0;
  margin: 0;
  border-bottom: 1px solid var(--secondaryColor);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const CustomNavTabs = styled(NavTabs)`
  transform: translateY(0px);
`;

// const Tooltip = ({ text }) => {
//   const tooltipStyle = {
//     position: 'absolute',
//     bottom: '-68%',
//     left: '75%',
//     transform: 'translateX(-50%)',
//     backgroundColor: 'white',
//     color: 'var(--headingColor);',
//     fontWeight: 'bold',
//     padding: '5px 10px',
//     border: '1px solid white',
//     borderRadius: '4px',
//     fontSize: '14px',
//     whiteSpace: 'nowrap'
//   };

//   return <div style={tooltipStyle}>{text}</div>;
// };

const Tooltip = ({ text }) => {
  const tooltipStyle = {
    zIndex: 10,
    backgroundColor: '#ffffff',
    border: '1px solid #efefef',
    boxSizing: 'border-box',
    boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.15)',
    padding: '5px 10px',
    position: 'absolute',
    transform: 'translateX(-43%) translateY(calc(-100% - 2px))',
    bottom: '-10%', // Position the tooltip correctly
    left: '80%',
    fontWeight: 'bold',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    color: 'var(--headingColor)'
  };

  const triangleStyle = {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    marginLeft: '-10px',
    borderWidth: '5px',
    borderStyle: 'solid',
    borderColor: '#ffffff transparent transparent transparent'
  };

  return (
    <div style={tooltipStyle}>
      {text}
      <div style={triangleStyle}></div>
    </div>
  );
};

function InterviewTab({
  callOn,
  setStep,
  setCallOn,
  respondantInfo,
  projectRespondent_ID,
  id,
  setRespondantActive,
  InterviewSchedule_ID,
  fullScreen,
  cleanup,
  getAudioVideoState,
  setShowPrecallTest
}) {
  const userId = useSelector(state => state.LoginReducer.userId);
  const captureguideData = useSelector(state => state.GetCaptureGuideReducer.captureguideData);
  const dispatch = useDispatch();
  const location = useLocation();
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);

  const isRespondentRoute = location.pathname.includes('pre-call-resp');
  const isRespondent = respondantInfo?.isRead == 1 || isRespondentRoute;

  const [guideStep, setGuideStep] = useState(isRespondent ? 2000 : 1);

  const [selectedOptionData, setSelectedOptionData] = useState([]);
  const [selectedTextValue, setSelectedTextValue] = useState([]);
  const [transcriptedData, setTranscriptedData] = useState([]);
  const [shuffledCaptureGuideData, setShuffledCaptureGuideData] = useState(captureguideData);
  const transcriptedDataSelector = useSelector(state => state.ProjectReducer?.rtmTranscriptData);
  const transcriptedDataLastUpdated = useSelector(state => state.ProjectReducer?.lastUpdated);
  const messageContainerRef = useRef(null);
  const sendTranscriptData = useSelector(state => state.SendTranscriptReducer);

  const data = { user_ID: userId };

  const shuffleArray = (items) => {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const isRankingAnswerSubmitted = (questionId) => {
    const answer = selectedOptionData[questionId];
    return (
      answer &&
      Array.isArray(answer?.questionChoice_ID) &&
      answer.questionChoice_ID.length > 0 &&
      Array.isArray(answer?.questionRankScale_ID) &&
      answer.questionRankScale_ID.length > 0
    );
  };

  const isChoiceAnswerSubmitted = (questionId) => {
    const answer = selectedOptionData[questionId];
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return answer !== undefined && answer !== null && answer !== '';
  };

  const applyShuffleToGuideData = (guideData) => {
    if (!guideData) return guideData;
    
    return {
      ...guideData,
      captureGuide: (guideData.captureGuide || []).map(section => ({
        ...section,
        questionName_array: (section.questionName_array || []).map(question => {
          const questionTypeId = question?.questionType_ID;
          const isShuffleSupportedType = [1, 2, 7, 14].includes(questionTypeId);
          
          if (!isShuffleSupportedType || !Array.isArray(question?.choiceName_array)) {
            return question;
          }

          const isAlreadyAnswered =
            questionTypeId === 14
              ? isRankingAnswerSubmitted(question.question_ID)
              : isChoiceAnswerSubmitted(question.question_ID);

          // Once answered/submitted, keep stable order (no reshuffle on refresh)
          if (isAlreadyAnswered) {
            return question;
          }

          if (question.toShuffle === 1) {
            return {
              ...question,
              choiceName_array: shuffleArray(question.choiceName_array),
            };
          }

          return question;
        }),
      })),
    };
  };

  // useEffect(() => {
  //   dispatch({
  //     type: 'GET_RESOLUTION',
  //     payload: data
  //   });
  // });

  useEffect(() => {
    dispatch({
      type: 'GET_RESOLUTION',
      payload: data
    });
    btnClickTranscriptData();
  }, []);

  useEffect(() => {
    if (captureguideData) {
      const shuffledData = applyShuffleToGuideData(captureguideData);
      setShuffledCaptureGuideData(shuffledData);
    }
  }, [captureguideData]);

  useEffect(() => {
    const jsonData = sendTranscriptData?.transcriptData;
    if (!jsonData)  return;
    try {
     
      // setTranscriptedData([...transcriptedData, ...jsonData]);

      setTranscriptedData(prev => {
        const merged = [...prev, ...jsonData];

        const uniqueMap = new Map();

        merged.forEach(item => {
          const key = `${item.text}-${item.name}-${item.end}`;
          uniqueMap.set(key, item);
        });

        return Array.from(uniqueMap.values());
      });

    } catch (error) {
      console.error("Invalid JSON in transcripted data:", error);
    }
  }, [sendTranscriptData?.lastUpdated]);

  useEffect(() => {
    const jsonData = transcriptedDataSelector?.Response?.json_data;
    
  
    if (!jsonData) { setTranscriptedData([]); return; }
  
    try {
      // // reset first
      // setTranscriptedData(null);
  
      // then set parsed data
      setTranscriptedData(JSON.parse(jsonData));
    } catch (error) {
      console.error("Invalid JSON in transcripted data:", error);
    }
  }, [transcriptedDataLastUpdated]);

  const btnClickTranscriptData = () => {
    const transcriptPayload = {
      user_ID: userId,
      InterviewSchedule_ID: respondantInfo.InterviewSchedule_ID,
      channel_name: respondantInfo.agoraChannelName,
      transcript_data: {}
    };
    dispatch({
      type: 'GET_RTM_TRANSCRIPT_DATA',
      payload: transcriptPayload
    });
  };

  // const handleTabClick = index => {
  //   setGuideStep(index + 1); // Always update guideStep
  //   if (callOn && !isSectionCompleteTab && !isSectionComplete && respondantInfo?.isInterviewStart == 1) {
  //     setShowTooltip(true);
  //     setTimeout(() => setShowTooltip(false), 3000);
  //   }
  // };

  useEffect(() => {
    const checkIsRespondent = respondantInfo?.isRead == 1 || isRespondentRoute;
    if (checkIsRespondent) {
      setGuideStep(2000);
    }
  }, [respondantInfo?.isRead, isRespondentRoute]);

  useEffect(() => {
    // For respondents, always set to Transcript tab (2000)
    if (isRespondent) {
      setGuideStep(2000);
    } else if (callOn && respondantInfo?.isInterviewStart == 1) {
      // For researchers, set to first section when call starts
      setGuideStep(1);
    }
  }, [callOn, respondantInfo?.isInterviewStart, isRespondent]);

  useEffect(() => {
    localStorage.setItem(
      'channel',
      respondantInfo?.agoraChannelName == undefined ? 'Test' : respondantInfo?.agoraChannelName
    );
    //localStorage.setItem('channel', respondantInfo?.agorachannelname == undefined ? 1806092021063926 : respondantInfo?.agorachannelname);
    localStorage.setItem('baseMode', 'avc');
    localStorage.setItem('transcode', 'interop');
    localStorage.setItem('attendeeMode', 'video');
    localStorage.setItem('videoProfile', '480p_4');
    localStorage.setItem('projectRespondent_ID', projectRespondent_ID);
    localStorage.setItem(
      'InterviewSchedule_ID',
      InterviewSchedule_ID != undefined ? InterviewSchedule_ID : InterviewSchedule_ID
    );
  });

  const scrollToBottom = () => {
    if (messageContainerRef.current && guideStep == 2000) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  };

  const callCaptureGuide = () => {
    dispatch({
      type: REQUEST_CAPTURE_GUIDE,
      payload: {
        user_ID: userId,
        project_ID: id,
        projectRespondent_ID: projectRespondent_ID
      }
    });
  };

  return (
    <div className="row px-3 py-2">
      <div className={`${fullScreen ? 'col-md-8' : 'col-md-7'}`}>
        <VideoCall
          setRespondantActive={setRespondantActive}
          setCallOn={setCallOn}
          setStep={setStep}
          callOn={callOn}
          agoraChannelName={respondantInfo?.agoraChannelName}
          respondantInfo={respondantInfo}
          id={id}
          fullScreen={fullScreen}
          cleanup={cleanup}
          getAudioVideoState={getAudioVideoState}
          setShowPrecallTest={setShowPrecallTest}
          unansweredCount={unansweredCount}
        />
      </div>
      <div className={`${fullScreen ? 'col-md-4' : 'col-md-5'}`} style={fullScreen ? { padding: 0 } : {}}>
        <Heading type={3} className="mb-0 pb-0 mt-4" fontWeight="700" fontSize="0.75rem" color="var(--deepBlue)">
          Capture Guide
        </Heading>
        {captureguideData ? (
          <Container className="mt-2" style={{ alignItems: 'end' }}>
            {!isRespondent && (
              <NavTabsContainer>
                <CustomNavTabs>
                  {captureguideData?.captureGuide?.map((section, sIndex) => (
                    <NavTab
                      className={sIndex == 0 ? '' : ''}
                      fontSize="0.73rem"
                      active={sIndex + +1 === guideStep}
                      onClick={() => setGuideStep(sIndex + +1)}
                    >
                      {section.sectionName}
                    </NavTab>
                  ))}
                </CustomNavTabs>
              </NavTabsContainer>
            )}
            <NavTabsContainer>
              <CustomNavTabs>
                <NavTab fontSize="0.73rem" active={2000 === guideStep} onClick={() => setGuideStep(2000)}>
                  Transcript
                </NavTab>
              </CustomNavTabs>
            </NavTabsContainer>
          </Container>
        ) : (
          <>
            {!isRespondent && (
              <>
                <Button
                  className="mt-2"
                  size="small"
                  onClick={callCaptureGuide}
                  variant="fill"
                  style={{ color: 'white' }}
                  buttonColor="var(--sapGreen)"
                >
                  Request Capture Guide
                </Button>
                <PageLoader2 />
              </>
            )}

            {/* {callCaptureGuide}   */}
          </>
        )}
        {captureguideData ? (
          <div
            style={{
              minHeight: 480,
              borderBottom: '1px solid var(--greyBlue)',
              maxHeight: 450,
              overflowY: 'auto',
              position: 'relative'
            }}
            ref={messageContainerRef}
            className="py-2"
          >
            <GuidedScreen
              setSelectedOptionData={setSelectedOptionData}
              selectedOptionData={selectedOptionData}
              setSelectedTextValue={setSelectedTextValue}
              selectedTextValue={selectedTextValue}
              step={guideStep}
              callOn={callOn == 1 && respondantInfo?.isInterviewStart == 1 ? true : false}
              id={id}
              respondantInfo={respondantInfo}
              fullScreen={fullScreen}
              transcriptedData={transcriptedData}
              scrollToBottom={scrollToBottom}
              unansweredCount={unansweredCount}
              setUnansweredCount={setUnansweredCount}
              captureguideData={shuffledCaptureGuideData}
            />
          </div>
        ) : (
          ''
        )}
        {captureguideData ? (
          <div
            className={`py-2 d-flex ${
              callOn && guideStep == captureguideData?.captureGuide?.length
                ? 'justify-content-between'
                : 'justify-content-end '
            }  align-items-center`}
          >
            {!isRespondent && callOn && guideStep == captureguideData?.captureGuide?.length && (
              <div>
                <Heading type={3} fontSize="0.75rem" fontWeight="500" color="var(--seaBlue)" className="">
                  You've reached the end of the capture guide
                </Heading>
              </div>
            )}

            <div className="d-flex justify-content-end align-items-center">
              {!isRespondent && guideStep !== 2000 && (
                <CustomButton
                  fontWeight="500"
                  fontSize="0.75rem"
                  size="small"
                  variant="outlined"
                  buttonColor="var(--sapGreen)"
                  disabled={guideStep == 1 ? true : false}
                  onClick={() => setGuideStep(prev => prev - 1)}
                >
                  <Icon style={{ fontSize: 10 }}>arrow_back_ios</Icon> Back
                </CustomButton>
              )}
              {!isRespondent && guideStep !== 2000 && (
                <CustomButton
                  className="ml-2"
                  fontWeight="500"
                  fontSize="0.75rem"
                  size="small"
                  buttonColor="var(--sapGreen)"
                  variant="outlined"
                  disabled={guideStep == captureguideData?.captureGuide?.length ? true : false}
                  onClick={() => setGuideStep(prev => prev + 1)}
                >
                  Next <Icon style={{ fontSize: 10 }}>arrow_forward_ios</Icon>
                </CustomButton>
              )}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
}

export default InterviewTab;
